"use client";

// Live prototype stage for prototype-primary case studies (see LiveDemo in
// data/projects.ts). Poster-first: the iframe mounts only on launch, so a case
// study embedding two full app routes doesn't boot them with the modal.
// The embedded route enforces its own server-side gate; the case-study unlock
// mints its session cookie (linkedExperiments) before this ever renders.

import { useState } from "react";
import type { LiveDemo } from "@/data/projects";
import { Button } from "@/components/ui/Button";

interface LiveDemoStageProps {
  demo: LiveDemo;
  themeColor: string;
}

export function LiveDemoStage({ demo, themeColor }: LiveDemoStageProps) {
  const [launched, setLaunched] = useState(false);
  const aspect = demo.aspect ?? "16/9";

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-3">
        <p className="text-[rgba(var(--foreground-rgb),0.5)] text-sm uppercase tracking-[0.15em] font-bold">
          <span
            className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
            style={{ backgroundColor: themeColor }}
            aria-hidden="true"
          />
          Live Prototype · {demo.title}
        </p>
        {launched && (
          <a
            href={demo.src}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[rgba(var(--foreground-rgb),0.6)] hover:text-[var(--foreground)] transition-colors duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background)] focus-visible:ring-[var(--theme-base)] rounded-sm"
          >
            Open full screen ↗
          </a>
        )}
      </div>

      <div
        className="relative w-full rounded-2xl overflow-hidden border border-[var(--surface-border)]"
        style={{ aspectRatio: aspect, minHeight: "360px" }}
      >
        {launched ? (
          <iframe
            src={demo.src}
            title={`Live prototype: ${demo.title}`}
            className="absolute inset-0 w-full h-full border-0"
            allow="fullscreen"
          />
        ) : (
          <div
            className="glass no-hover absolute inset-0 flex flex-col items-center justify-center gap-5 p-6 text-center"
            style={{
              isolation: "isolate",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }}
          >
            {demo.blurb && (
              <p className="text-[rgba(var(--foreground-rgb),0.7)] text-base leading-relaxed max-w-[440px] text-pretty">
                {demo.blurb}
              </p>
            )}
            {demo.external ? (
              <Button as="a" variant="primary" href={demo.src} target="_blank" rel="noopener noreferrer">
                Open the prototype ↗
              </Button>
            ) : (
              <Button variant="primary" onClick={() => setLaunched(true)}>
                Launch the prototype
              </Button>
            )}
          </div>
        )}
      </div>

      {demo.caption && (
        <p className="text-[rgba(var(--foreground-rgb),0.5)] text-sm mt-3 text-center italic">
          {demo.caption}
        </p>
      )}
    </div>
  );
}
